import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCurrency } from '../../lib/utils';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Calculator
} from 'lucide-react';

const DashboardSummary = ({ stats }) => {
  const totalIncome = (stats.paidAmountAfterVatWht || 0) + stats.paidCompanyFee;
  const totalExpenses = stats.totalPaidExpenses;
  const grossProfit = totalIncome - totalExpenses;
  const profitMargin = totalIncome > 0 ? Math.round((grossProfit / totalIncome) * 100) : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Income Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>สรุปรายได้</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-700">รายได้จริงที่ชำระแล้ว</p>
                  <p className="text-xs text-green-600">หัก VAT และ WHT ที่ชำระแล้ว</p>
                </div>
                <p className="text-xl font-bold text-green-800">
                  {formatCurrency(stats.paidAmountAfterVatWht || 0)}
                </p>
              </div>
            </div>
            
            <div className="p-4 bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-indigo-700">ค่าบริการบริษัท</p>
                  <p className="text-xs text-indigo-600">รายได้บริษัท</p>
                </div>
                <p className="text-xl font-bold text-indigo-800">
                  {formatCurrency(stats.totalCompanyFee)}
                </p>
              </div>
            </div>
          </div>
          
            <div className="p-4 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg border border-emerald-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-emerald-700">รายได้รวมที่ชำระแล้ว</p>
                  <p className="text-sm text-emerald-600">รายได้จริงที่ชำระแล้ว + ค่าบริการบริษัทที่จ่ายแล้ว</p>
                </div>
                <p className="text-2xl font-bold text-emerald-800">
                  {formatCurrency(totalIncome)}
                </p>
              </div>
            </div>
        </CardContent>
      </Card>

      {/* Expenses Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingDown className="h-5 w-5" />
            <span>สรุปค่าใช้จ่าย</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg border border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-700">ค่าจ้าง Developer</p>
                  <p className="text-xs text-orange-600">จ่ายแล้ว: {formatCurrency(stats.paidDeveloperPayments)}</p>
                </div>
                <p className="text-xl font-bold text-orange-800">
                  {formatCurrency(stats.totalDeveloperPayments)}
                </p>
              </div>
            </div>
            
            <div className="p-4 bg-gradient-to-r from-red-50 to-red-100 rounded-lg border border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-700">ค่าใช้จ่ายอื่นๆ</p>
                  <p className="text-xs text-red-600">จ่ายแล้ว: {formatCurrency(stats.paidOtherExpenses)}</p>
                </div>
                <p className="text-xl font-bold text-red-800">
                  {formatCurrency(stats.totalOtherExpenses)}
                </p>
              </div>
            </div>
            
            <div className="p-4 bg-gradient-to-r from-cyan-50 to-cyan-100 rounded-lg border border-cyan-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-cyan-700">Commission Sale</p>
                  <p className="text-xs text-cyan-600">จ่ายแล้ว: {formatCurrency(stats.paidSalesCommission)}</p>
                </div>
                <p className="text-xl font-bold text-cyan-800">
                  {formatCurrency(stats.totalSalesCommission)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-gradient-to-r from-rose-50 to-rose-100 rounded-lg border border-rose-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-semibold text-rose-700">ค่าใช้จ่ายรวม</p>
                <p className="text-sm text-rose-600">จ่ายแล้ว: {formatCurrency(stats.totalPaidExpenses)}</p>
              </div>
              <p className="text-2xl font-bold text-rose-800">
                {formatCurrency(totalExpenses)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profit Summary */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calculator className="h-5 w-5" />
            <span>สรุปกำไรขาดทุน</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200 text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <DollarSign className="h-5 w-5 text-blue-600" />
                <p className="text-sm font-medium text-blue-700">รายได้รวมที่ชำระแล้ว</p>
              </div>
              <p className="text-2xl font-bold text-blue-800">
                {formatCurrency(totalIncome)}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                รายได้จริงที่ชำระแล้ว + ค่าบริการบริษัทที่จ่ายแล้ว
              </p>
            </div>

            <div className="p-4 bg-gradient-to-r from-red-50 to-red-100 rounded-lg border border-red-200 text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <TrendingDown className="h-5 w-5 text-red-600" />
                <p className="text-sm font-medium text-red-700">ค่าใช้จ่ายรวม</p>
              </div>
              <p className="text-2xl font-bold text-red-800">
                {formatCurrency(totalExpenses)}
              </p>
              <p className="text-xs text-red-600 mt-1">
                Developer + อื่นๆ + Commission
              </p>
            </div>

            <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200 text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                <p className="text-sm font-medium text-green-700">กำไรสุทธิ</p>
              </div>
              <p className={`text-2xl font-bold ${grossProfit >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                {formatCurrency(grossProfit)}
              </p>
              <p className={`text-xs mt-1 ${profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {profitMargin >= 0 ? '+' : ''}{profitMargin}% กำไร
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardSummary;
